import { useEffect, useRef, useState } from 'react';

export const COMMUNITY_MEDIA_TIMEOUT = 20000;

// Prepare the fetched page, not the entire community history. Limit simultaneous
// image requests and decode before revealing the timeline (including cached media).
export function prepareCommunityImages(urls, signal, timeout = COMMUNITY_MEDIA_TIMEOUT) {
  return new Promise(resolve => {
    const queue = [...new Set(urls.filter(Boolean))];
    const results = {};
    const pending = new Map();
    let cursor = 0;
    let finished = false;
    let timer;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      pending.forEach(image => { image.onload = null; image.onerror = null; image.removeAttribute('src'); });
      queue.forEach(url => { if (!(url in results)) results[url] = false; });
      resolve(results);
    };
    const pump = () => {
      if (finished) return;
      while (pending.size < 6 && cursor < queue.length) {
        const url = queue[cursor++];
        const image = new Image();
        pending.set(url, image);
        const settle = success => {
          if (finished || !pending.has(url)) return;
          results[url] = success;
          image.onload = null; image.onerror = null;
          pending.delete(url);
          pump();
        };
        let decoding = false;
        const loaded = () => {
          if (decoding || finished) return;
          decoding = true;
          if (typeof image.decode === 'function') {
            image.decode().then(() => settle(true), () => settle(false));
          } else settle(image.naturalWidth > 0);
        };
        image.onload = loaded;
        image.onerror = () => settle(false);
        image.src = url;
        if (image.complete && image.naturalWidth > 0) loaded();
      }
      if (!pending.size && cursor === queue.length) finish();
    };
    signal.addEventListener('abort', finish, { once: true });
    timer = setTimeout(finish, timeout);
    if (signal.aborted) finish();
    else pump();
  });
}

export default function useCommunityMediaReady(urls, refreshKey) {
  const key = JSON.stringify([...new Set(urls.filter(Boolean))].sort());
  const cache = useRef({ refreshKey, results: {} });
  const [snapshot, setSnapshot] = useState(null);
  const empty = key === '[]';
  useEffect(() => {
    if (cache.current.refreshKey !== refreshKey) cache.current = { refreshKey, results: {} };
    const controller = new AbortController();
    const missing = JSON.parse(key).filter(url => !(url in cache.current.results));
    prepareCommunityImages(missing, controller.signal).then(results => {
      if (controller.signal.aborted) return;
      cache.current.results = { ...cache.current.results, ...results };
      setSnapshot({ key, refreshKey, results: cache.current.results });
    });
    return () => controller.abort();
  }, [key, refreshKey]);
  return {
    ready: empty || (snapshot?.key === key && snapshot?.refreshKey === refreshKey),
    failed: snapshot?.results || {},
  };
}
